import { useExtendedAccountsFilters } from '@/ui/features/contact-management/AccountsPanel/hooks';
import { FilterChip, FilterConfig, useFilterConfig } from '@juvare/common-data-table-ui/FiltersBar';
import { useResolvedAccountFilterConfig } from '@juvare/contact-management/ui/features/accounts/hooks';
import { AccountFilterData } from '@juvare/contact-management/ui/features/accounts/hooks/useAccountList';
import { useTranslation } from 'react-i18next';

export function useBackgroundCheckTableFilterConfig(requireOrgRoleInStatusFilter: boolean, numberOfActiveBackgroundCheckTypes: number) {
    const { extendFilters } = useExtendedAccountsFilters();
    const { t } = useTranslation();

    const backgroundCheckFilterConfig = useFilterConfig((builder) => {
        extendFilters(builder, {
            addBackgroundCheckStatusQuickFilter: true,
            addBackgroundCheckTypeQuickFilter: numberOfActiveBackgroundCheckTypes > 1,
        });

        builder.quickDateRangeFilter({
            dataPath: 'backgroundCheckUpdatedAt',
            label: t('accountsFilters.backgroundCheck.lastUpdate'),
            sx: { maxWidth: { xs: 1, sm: 280 }, width: 1 },
        });
    }, []);

    const accountFilterConfig = useResolvedAccountFilterConfig({
        requireOrgRoleInStatusFilter,
        showAccountSystemRoleFilter: 'onlyAllFiltersPanel',
    });

    return {
        allFilters:
            accountFilterConfig.allFilters?.children || backgroundCheckFilterConfig?.allFilters?.children
                ? {
                      type: 'filter.form',
                      children: [
                          ...(accountFilterConfig.allFilters?.children ?? []),
                          ...(backgroundCheckFilterConfig?.allFilters?.children ?? []),
                      ],
                  }
                : undefined,
        getFilterChips:
            accountFilterConfig.getFilterChips || backgroundCheckFilterConfig.getFilterChips
                ? async (...params) => {
                      const accountFilterChips = (await accountFilterConfig.getFilterChips?.(...params)) ?? [];
                      const licenseCheckLatestStatusFilterChips = (await backgroundCheckFilterConfig.getFilterChips?.(...params)) ?? [];
                      return [...accountFilterChips, ...licenseCheckLatestStatusFilterChips].reduce<FilterChip<object>[]>((acc, chip) => {
                          if (acc.some(({ groupId, label }) => groupId === chip.groupId && label === chip.label)) {
                              return acc;
                          }
                          return [...acc, chip];
                      }, []);
                  }
                : undefined,
        quickFilters:
            accountFilterConfig.quickFilters || backgroundCheckFilterConfig.quickFilters
                ? [...(accountFilterConfig.quickFilters ?? []), ...(backgroundCheckFilterConfig.quickFilters ?? [])]
                : undefined,
        filterDependencies:
            accountFilterConfig.filterDependencies || backgroundCheckFilterConfig.filterDependencies
                ? {
                      ...(accountFilterConfig.filterDependencies ?? {}),
                      ...(backgroundCheckFilterConfig.filterDependencies ?? {}),
                  }
                : undefined,
    } satisfies FilterConfig<AccountFilterData>;
}
